import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Eye, Heart, Star, X } from 'lucide-react';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import DocumentPreviewModal from '../../components/ui/DocumentPreviewModal';
import Table, { type TableColumn } from '../../components/ui/Table';
import favoritesService from '../../services/favoritesService';
import type { FavoriteDocument } from '../../types/favorites.types';
import { formatDocumentDisplayName } from '../../utils/documentName';
import { canPreviewDocument } from '../../utils/documentAccessMode';

const PAGE_SIZE = 10;

const FavoritesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [previewDocId, setPreviewDocId] = useState<number | string | null>(
    null
  );

  useEffect(() => {
    document.title = 'RRMS | Favorites';
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const favoritesQuery = useQuery({
    queryKey: ['favorites', debouncedSearch, page],
    queryFn: () =>
      favoritesService.getFavorites({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const removeMutation = useMutation({
    mutationFn: (documentId: number) =>
      favoritesService.removeFavorite(documentId),
    onSuccess: () => {
      toast.success('Removed from favorites');
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message || 'Failed to remove from favorites.';
      toast.error(msg);
    },
  });

  const data = favoritesQuery.data;
  const items: FavoriteDocument[] = data?.data ?? [];
  const pagination = data?.pagination ?? {
    page,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  const handleRemove = useCallback((documentId: number) => {
    if (removeMutation.isPending) return;
    removeMutation.mutate(documentId);
  }, [removeMutation]);

  const handleOpenPreview = useCallback((documentId: number) => {
    setPreviewDocId(documentId);
  }, []);

  const isLoading = favoritesQuery.isLoading || favoritesQuery.isFetching;
  const isError = favoritesQuery.isError;

  const columns: TableColumn<FavoriteDocument>[] = useMemo(
    () => [
      {
        key: 'file_name',
        header: 'File Name',
        minWidth: 220,
        clampLines: 2,
        tooltip: (doc) =>
          formatDocumentDisplayName({
            cmsNumber: doc.cms_number,
            originalFileName: doc.file_name,
            documentType: doc.document_type,
          }),
        render: (doc) => (
          <button
            type="button"
            onClick={() => handleOpenPreview(doc.document_id)}
            className="text-left font-medium text-[var(--color-primary)] hover:underline"
          >
            {formatDocumentDisplayName({
              cmsNumber: doc.cms_number,
              originalFileName: doc.file_name,
              documentType: doc.document_type,
            })}
          </button>
        ),
      },
      {
        key: 'file_number',
        header: 'File No',
        minWidth: 150,
        tooltip: (doc) =>
          `${(doc as any).file_number || doc.cms_number || '—'}${doc.rrms_ref_number ? ` | ${doc.rrms_ref_number}` : ''}`,
        render: (doc) => (
          <>
            <div className="font-mono text-sm">{(doc as any).file_number || doc.cms_number || '—'}</div>
            <div className="text-[15px] text-muted">{doc.rrms_ref_number || '—'}</div>
          </>
        ),
      },
      {
        key: 'hierarchy_path',
        header: 'Section',
        minWidth: 180,
        clampLines: 2,
        tooltip: (doc) => (doc as any).hierarchy_path || doc.ps_name || '—',
        render: (doc) => (doc as any).hierarchy_path || doc.ps_name || '—',
      },
      {
        key: 'category',
        header: 'Category',
        minWidth: 120,
        clampLines: 2,
        tooltip: (doc) => (doc as any).category || '—',
        render: (doc) => (doc as any).category || '—',
      },
      {
        key: 'subject',
        header: 'Subject',
        minWidth: 180,
        clampLines: 2,
        tooltip: (doc) => (doc as any).subject || '—',
        render: (doc) => (doc as any).subject || '—',
      },
      {
        key: 'classification',
        header: 'Classification',
        minWidth: 140,
        tooltip: (doc) => doc.classification,
        render: (doc) => (
          <Badge color={doc.classification === 'Confidential' ? 'error' : 'success'}>
            {doc.classification}
          </Badge>
        ),
      },
      {
        key: 'favorited_at',
        header: 'Date Added',
        minWidth: 160,
        tooltip: (doc) =>
          doc.favorited_at ? format(new Date(doc.favorited_at), 'dd MMM yyyy, HH:mm') : '—',
        render: (doc) =>
          doc.favorited_at ? format(new Date(doc.favorited_at), 'dd MMM yyyy, HH:mm') : '—',
      },
      {
        key: 'actions',
        header: 'Actions',
        minWidth: 120,
        tooltip: () => 'Preview or remove from favorites',
        render: (doc) => {
          const previewAllowedByType = canPreviewDocument({ fileName: doc.file_name });

          return (
            <div className="inline-flex items-center gap-2">
              {previewAllowedByType && (
                <button
                  type="button"
                  onClick={() => handleOpenPreview(doc.document_id)}
                  className="inline-flex items-center justify-center rounded-full p-1.5 text-[var(--color-primary)] hover:text-[var(--color-primary-light)]"
                  title="Preview"
                >
                  <Eye size={18} />
                </button>
              )}
              <button
                type="button"
                onClick={() => handleRemove(doc.document_id)}
                disabled={removeMutation.isPending}
                className="inline-flex items-center justify-center rounded-full p-1.5 text-yellow-500 hover:text-yellow-600 disabled:opacity-50"
                title="Remove from favorites"
              >
                <Star size={18} className="fill-current" />
              </button>
            </div>
          );
        },
      },
    ],
    [handleOpenPreview, handleRemove, removeMutation.isPending]
  );

  return (
    <div className="">
      <PageHeader
        title="Favorites"
        subtitle="Documents you have starred"
        actions={
          <div className="relative w-full md:w-72 lg:w-80">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search favorites (file name / CMS number / PS name)..."
              style={{
                width: '100%',
                paddingLeft: 36,
                paddingRight: searchText ? 34 : 12,
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-md)',
                background: 'white',
              }}
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'transparent',
                  padding: 4,
                  cursor: 'pointer',
                  color: 'var(--color-gray-500)',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        }
      />

      {isLoading && (
        <div className="card p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-[var(--color-gray-100)]" style={{ marginBottom: i === 4 ? 0 : 'var(--spacing-3)' }} />
          ))}
        </div>
      )}

      {!isLoading && isError && (
        <div className="card p-8 text-center">
          <p className="text-md text-[var(--color-error)]">
            Failed to load favorites. Please try again.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className="card p-10 text-center">
          <Heart
            size={40}
            className="mx-auto mb-4 text-[var(--color-gray-300)]"
          />
          <p className="text-md font-semibold text-[var(--color-gray-700)]">
            No favorite documents yet.
          </p>
          <p className="mt-1 text-sm text-muted">
            Star documents to find them here.
          </p>
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <Table
          columns={columns}
          rows={items}
          rowKey={(doc) => String(doc.document_id)}
          pagination={pagination}
          onPageChange={setPage}
          minWidth={900}
        />
      )}

      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => setPreviewDocId(null)}
      />
    </div>
  );
};

export default FavoritesPage;
